"use client";

// THE SORT LINE — under the view chips, in the navigator (John, 18 oct; seated
// in the head on 19 oct so it stays while the folder scrolls): `sorted by <b>last touched</b> ⌄ newest first` (the count is the
// head's stat line already, said once), the word opening THE
// frosted menu (OptionsMenu: anchored on a desktop, the sheet on a phone) with
// the orders that matter here. WHICH WAY it runs is not on this line: press the
// selected view chip again and its arrow turns over (SortArrow, below).

import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useOsShell } from "./OsShellContext";
import { useChipRow } from "./chipRow";
import OptionsMenu from "./artifact/OptionsMenu";
import type { MenuItem } from "./ContextMenu";
import { Chevron } from "../ui";
import { SORT_OPTIONS, dirWords, naturalDir, type FolderSort } from "../../lib/os/folderSort";

export default function FolderSortLine({ sort, onSort }: { sort: FolderSort; onSort: (next: FolderSort) => void }) {
  // the line stands in the navigator, under the chips (the shell's chip slot),
  // so the order is in reach however far the folder is scrolled; off the shell
  // it stands where it is written
  const { chipSlot } = useOsShell();
  const watchLine = useChipRow();
  const ref = useRef<HTMLButtonElement | null>(null);
  const [anchor, setAnchor] = useState<{ x: number; y: number; bottom: number } | null>(null);
  const label = SORT_OPTIONS.find((o) => o.id === sort.key)?.label ?? SORT_OPTIONS[0].label;
  const toggle = () => {
    const r = ref.current?.getBoundingClientRect();
    setAnchor((a) => (a || !r ? null : { x: r.left, y: r.top, bottom: r.bottom }));
  };
  const items: MenuItem[] = SORT_OPTIONS.map((o) => {
    const on = o.id === sort.key;
    return {
      label: o.label,
      selected: on,
      icon: on ? <span className="ink-dot" aria-hidden /> : undefined,
      // a new order starts at its natural end; the chip's arrow turns it over
      onSelect: () => onSort(on ? sort : { key: o.id, dir: naturalDir(o.id) }),
    };
  });
  const line = (
    <div ref={chipSlot ? watchLine : undefined} className="fs-line co-meta" role="group" aria-label="how this folder is ordered">
      <button ref={ref} type="button" className="os-link fs-line__by" aria-haspopup="menu" aria-expanded={!!anchor} onClick={toggle}>
        <span className="os-link-text">
          sorted by <b>{label}</b>
        </span>
        <Chevron size={12} dir="down" />
      </button>
      <span className="fs-line__dir">{dirWords(sort)}</span>
      <OptionsMenu open={!!anchor} onClose={() => setAnchor(null)} items={items} anchor={anchor} title="sort by" />
    </div>
  );
  return chipSlot ? createPortal(line, chipSlot) : line;
}

/** the selected view chip's arrow: down runs newest (or folders, or z) first,
 *  up the other way. It turns over, never swaps, so the change reads as one. */
export function SortArrow({ sort }: { sort: FolderSort }) {
  return (
    <span className="fs-arrow" data-dir={sort.dir} role="img" aria-label={dirWords(sort)}>
      <svg viewBox="0 0 12 12" width="12" height="12" fill="none" aria-hidden>
        <path d="M6 2.2v7.4M2.9 6.6 6 9.7l3.1-3.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}
